import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { useResetPassword } from '../api';
import { useToast } from '@/hooks/useToast';
import { AuthShell, FloatField, PwStrengthMeter } from '../AuthShared';

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const passwordFieldSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Must contain an uppercase letter')
  .regex(/[a-z]/, 'Must contain a lowercase letter')
  .regex(/\d/, 'Must contain a number');

const confirmPasswordFieldSchema = z
  .string()
  .min(8, 'Confirm password must be at least 8 characters');

export const ResetPasswordPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const params = useQueryParams();
  const token = params.get('token') || '';
  const resetMutation = useResetPassword();

  const form = useForm({
    defaultValues: { password: '', confirmPassword: '' },
    onSubmit: async ({ value }) => {
      try {
        const { message } = await resetMutation.mutateAsync({
          token,
          newPassword: value.password,
        });
        toast.success(message || 'Password reset successful. Please sign in.');
        navigate('/login');
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error?.message ||
            error?.message ||
            'Failed to reset password. Please try again.'
        );
      }
    },
  });

  return (
    <AuthShell>
      <div className="head">
        <h1>Reset your password</h1>
        <p>Choose a strong new password for your account.</p>
      </div>

      {!token && (
        <div className="auth-error-banner" role="alert">
          This reset link is invalid or has expired. Please{' '}
          <Link to="/forgot-password" style={{ color: 'var(--danger)', fontWeight: 600 }}>
            request a new one
          </Link>
          .
        </div>
      )}

      {resetMutation.error && (
        <div className="auth-error-banner" role="alert">
          {(resetMutation.error as any)?.response?.data?.error?.message ||
            'Reset failed. Please try again.'}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        noValidate
      >
        <form.Field
          name="password"
          validators={{
            onBlur: ({ value }) => {
              const r = passwordFieldSchema.safeParse(value);
              return r.success ? undefined : r.error.issues[0]?.message;
            },
            onSubmit: ({ value }) => {
              const r = passwordFieldSchema.safeParse(value);
              return r.success ? undefined : r.error.issues[0]?.message;
            },
          }}
        >
          {(field) => (
            <>
              <FloatField
                id="reset-password"
                label="New password"
                type="password"
                icon="lock"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                autoComplete="new-password"
                error={field.state.meta.errors[0] as string | undefined}
              />
              <PwStrengthMeter pw={field.state.value} />
            </>
          )}
        </form.Field>

        <div style={{ marginTop: 14 }}>
          <form.Field
            name="confirmPassword"
            validators={{
              onBlur: ({ value, fieldApi }) => {
                const r = confirmPasswordFieldSchema.safeParse(value);
                if (!r.success) return r.error.issues[0]?.message;
                const password = fieldApi.form.getFieldValue('password' as any);
                if (value !== password) return "Passwords don't match";
                return undefined;
              },
              onSubmit: ({ value, fieldApi }) => {
                const r = confirmPasswordFieldSchema.safeParse(value);
                if (!r.success) return r.error.issues[0]?.message;
                const password = fieldApi.form.getFieldValue('password' as any);
                if (value !== password) return "Passwords don't match";
                return undefined;
              },
            }}
          >
            {(field) => (
              <FloatField
                id="reset-confirmPassword"
                label="Confirm new password"
                type="password"
                icon="lock"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                autoComplete="new-password"
                error={field.state.meta.errors[0] as string | undefined}
              />
            )}
          </form.Field>
        </div>

        <button
          type="submit"
          disabled={resetMutation.isPending || !token}
          className="btn-auth-primary auth-cta"
          style={{ marginTop: 18 }}
        >
          {resetMutation.isPending ? 'Resetting…' : 'Reset password'}
        </button>
      </form>

      <div className="auth-foot">
        Remembered it? <Link to="/login">Back to sign in</Link>
      </div>
    </AuthShell>
  );
};
